import {
  faChevronDown,
  faComment,
  faParagraph,
  faSearchPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress, useTheme } from '@material-ui/core';
import { orderBy } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../../../components/voxel/Buttons/VoxelIconButton';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelDialogSidePanel from '../../../../../components/voxel/Dialogs/VoxelDialogSidePanel';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import VoxelAdvancedTextSearch from '../../../../../components/voxel/Search/VoxelAdvancedTextSearch';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { QueuedItemStatus } from '../../../../../graphql/projects/models/QueuedItemStatus';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import ApproveContentButton from '../../Buttons/ContentTaskTable/ApproveContentButton';
import ContentKeywordAddTable from '../../Tables/ContentKeywordAddTable/ContentKeywordAddTable';
import { useContentKeywordSearchSelectBag } from '../../Tables/ContentKeywordAddTable/logic/useContentKeywordSearchSelectBag';
import { useContentKeywordSearchTableState } from '../../Tables/ContentKeywordAddTable/logic/useContentKeywordSearchTableState';
import ContentKeywordTable from '../../Tables/ContentKeywordTable/ContentKeywordTable';
import ContentUrlAddAllDialog from '../ContentUrlAddAllDialog';
import ContentTaskActionInput from './ContentTaskActionInput';
import ContentTaskChatGPT from './ContentTaskChatGPT';
import ContentTaskComment from './ContentTaskComment';
import ContentTaskMeta from './ContentTaskMeta/ContentTaskMeta';
import ContentTaskUrl from './ContentTaskUrl';
import { useAddKeywordSearch } from './useAddKeywordSearch';
import { useDragKeywordLogic } from './useDragKeywordLogic';

export interface ContentTaskDialogProps {
  project: ProjectGq;
  contentTask: ContentTaskGq;
  openDialog: boolean;
  close: () => void;
  clientUrl: string;
  reload: () => Promise<any>;
  showTags?: TagGroupGq[];
}

const ContentTaskDialog: React.FC<ContentTaskDialogProps> = ({
  project,
  contentTask,
  openDialog,
  close,
  clientUrl,
  reload,
  showTags,
}) => {
  // General state
  const closeDialog = () => {
    reload();
    setLocalAddKeywords([]);
    setLocalTaskKeywords([]);
    setAddSearchTerm('');
    setShowAddTable(false);
    close();
  };

  const [showAddSearchResultDialog, setShowAddSearchResultDialog] = useState(false);

  // Editing state
  const [updatingContentTask, setUpdatingContentTask] = useState(false);
  const [editUrl, setEditUrl] = useState(false);

  // Local task keywords
  // We use local state to instantly show updates, while they're being pushed to the backend
  const taskKeywordsSorted = useMemo(() => {
    return orderBy(contentTask.keywords, [(c) => c.order, (c) => c.id]);
  }, [contentTask.keywords]);

  useEffect(() => {
    setLocalTaskKeywords(taskKeywordsSorted);
  }, [taskKeywordsSorted]);

  const [localTaskKeywords, setLocalTaskKeywords] =
    useState<ContentKeywordGq[]>(taskKeywordsSorted);

  const [showComment, setShowComment] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  // ChatGPT
  const [showChatGptContent, setShowChatGptContent] = useState(false);

  // Add table
  // We use local state to instantly show updates, while they're being pushed to the backend
  const [showAddTable, setShowAddTable] = useState(false);
  const [localAddKeywords, setLocalAddKeywords] = useState<ContentKeywordGq[]>([]);
  const addTableState = useContentKeywordSearchTableState({
    invertSelection: false, //Not used
    contentKeywordIds: [],
    emptyIsAll: false,
  });

  // Add keyword search
  const { keywordQuery, addSearchTerm, setAddSearchTerm, debouncedSearchTerm, setPaging } =
    useAddKeywordSearch({
      contentTaskId: contentTask.id,
      projectId: contentTask.projectId,
      setLocalAddKeywords,
    });

  // Add select bag (tracking when user clicks and selects keywords in the "add window" below)
  const addTableSelectBag = useContentKeywordSearchSelectBag(addTableState, localAddKeywords);

  // Drag keyword logic (tracking what happens if a user drags keywords)
  const localReload = useCallback(
    () =>
      reload()
        .then(() => keywordQuery.query.refetch())
        .then(() => console.log('Reloaded')),
    [reload, keywordQuery.query.refetch]
  );
  const { handleDragEnd, handleDragStart, isDraggingGlobal, updatingDragKeywords } =
    useDragKeywordLogic({
      projectId: project.id,
      localAddKeywords,
      localTaskKeywords,
      setLocalAddKeywords,
      setLocalTaskKeywords,
      contentTaskId: contentTask.id,
      addTableSelectBag,
      addTableState,
      reload: localReload,
    });
  const updating = updatingContentTask || updatingDragKeywords;

  // Styling
  const theme = useTheme();
  let sidePanels: ReactElement[] = [];
  if (showMeta) {
    sidePanels.push(
      <VoxelDialogSidePanel handleClose={() => setShowMeta(false)}>
        <ContentTaskMeta
          contentTask={contentTask}
          reload={reload}
          updating={updating}
          setUpdating={setUpdatingContentTask}
        />
      </VoxelDialogSidePanel>
    );
  }
  if (showComment) {
    sidePanels.push(
      <VoxelDialogSidePanel handleClose={() => setShowComment(false)}>
        <ContentTaskComment
          newValue={contentTask.comment}
          reload={reload}
          contentTask={contentTask}
          updating={updating}
          setUpdating={setUpdatingContentTask}
          valueKey={'comment'}
        />
      </VoxelDialogSidePanel>
    );
  }
  if (showChatGptContent) {
    sidePanels.push(
      <VoxelDialogSidePanel handleClose={() => setShowChatGptContent(false)}>
        <ContentTaskChatGPT contentTask={contentTask} />
      </VoxelDialogSidePanel>
    );
  }

  return (
    <VoxelDialog
      maxWClass={showComment || showMeta || showChatGptContent ? 'max-w-8xl' : 'max-w-7xl'}
      open={openDialog}
      onClose={closeDialog}
      closeOnBackdrop={false}
      sidePanelsRight={sidePanels}
    >
      <VoxelDialogContent className="p-6">
        <div className="w-full h-full">
          <div className="w-full pr-8 flex flex-row flex-nowrap justify-between items-center gap-12">
            <ContentTaskUrl
              contentTask={contentTask}
              clientUrl={clientUrl}
              reload={localReload}
              editUrl={editUrl}
              setEditUrl={setEditUrl}
            />
            <div className="flex flex-row flex-nowrap items-center gap-4">
              <VoxelText variant={'bread-semibold'} noWrap>
                Action:{' '}
              </VoxelText>
              <ContentTaskActionInput
                contentTask={contentTask}
                newValue={contentTask.actionManual}
                originalValue={contentTask.action}
                changed={!!contentTask.actionManual}
                reload={reload}
                updating={updatingContentTask}
                setUpdating={setUpdatingContentTask}
              />
              <ApproveContentButton
                contentTask={contentTask}
                reload={localReload}
                disabled={updatingContentTask}
              />
            </div>
          </div>
          <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            <div className="relative mt-16 min-w-[1200px]">
              <div className="absolute right-3 -top-10 flex flex-row flex-nowrap gap-3">
                {!!contentTask.chatGptStatus && (
                  <VoxelButton
                    onClick={() => {
                      setShowChatGptContent(!showChatGptContent);
                    }}
                    disabled={!contentTask.chatGptStatus}
                    endIcon={
                      <FontAwesomeIcon
                        className={
                          contentTask.chatGptStatus === QueuedItemStatus.SUCCEEDED
                            ? 'text-success-600'
                            : contentTask.chatGptStatus === QueuedItemStatus.PROCESSING
                              ? 'text-warning-500'
                              : ''
                        }
                        icon={faRobot}
                      />
                    }
                    size="md"
                    title={'ChatGPT content'}
                    color={showChatGptContent ? 'primary' : 'light-gray'}
                  />
                )}
                <VoxelButton
                  onClick={() => setShowMeta(!showMeta)}
                  endIcon={<FontAwesomeIcon icon={faParagraph} />}
                  size="md"
                  title={'Meta'}
                  color={showMeta ? 'primary' : 'light-gray'}
                />
                <VoxelButton
                  onClick={() => setShowComment(!showComment)}
                  endIcon={<FontAwesomeIcon icon={faComment} />}
                  size="md"
                  title={'Comment'}
                  color={showComment ? 'primary' : 'light-gray'}
                />
                <VoxelButton
                  onClick={() => {
                    setShowAddTable(!showAddTable);
                    setAddSearchTerm('');
                  }}
                  endIcon={<FontAwesomeIcon icon={faSearchPlus} />}
                  size="md"
                  title={'Add keywords'}
                  color={showAddTable ? 'primary' : 'light-gray'}
                />
              </div>
              <ContentKeywordTable
                project={project}
                clientUrl={project.clientWebsite}
                contentTask={contentTask}
                reload={localReload}
                localItems={localTaskKeywords}
                setLocalItems={setLocalTaskKeywords}
                isAdding={showAddTable}
                isDragginGlobal={isDraggingGlobal}
                showTags={showTags}
              />
            </div>
            <div
              className={'shadow-[0_-20px_25px_-25px_rgba(0,0,0,0.3)]'}
              style={{ zIndex: theme.zIndex.modal + 1 }}
            >
              {showAddTable &&
                (((updatingContentTask || keywordQuery.query.loading) && <LinearProgress />) || (
                  <div className={'h-4'} />
                ))}
              <div>
                {showAddTable && (
                  <div>
                    <div className="flex flex-row flex-nowrap items-center justify-between">
                      <div className="flex flex-row items-center gap-4">
                        <div className="w-56">
                          <VoxelAdvancedTextSearch
                            size='sm'
                            value={addSearchTerm ?? ''}
                            onChange={(event) => setAddSearchTerm(event.target.value)}
                            placeholder={'Search keyword...'}
                            deleteValue={() => setAddSearchTerm('')}
                          />
                        </div>
                        {!!debouncedSearchTerm.length && keywordQuery.count > 0 && (
                          <VoxelButton
                            title="Add all "
                            size={'lg'}
                            endIcon={<FontAwesomeIcon icon={faSearchPlus} />}
                            onClick={() => setShowAddSearchResultDialog(true)}
                          />
                        )}
                      </div>
                      <VoxelIconButton
                        size="lg"
                        color="white"
                        onClick={() => {
                          setShowAddTable(false);
                          setAddSearchTerm('');
                        }}
                        icon={<FontAwesomeIcon icon={faChevronDown} size="lg" />}
                      />
                    </div>
                    <ContentKeywordAddTable
                      clientUrl={clientUrl}
                      project={project}
                      localAddKeywords={localAddKeywords}
                      debouncedSearchTerm={debouncedSearchTerm}
                      setPaging={setPaging}
                      keywordQueryRows={keywordQuery.rowsPerPage}
                      keywordQueryPage={keywordQuery.page}
                      keywordQueryCount={keywordQuery.count}
                      keywordQueryLoading={keywordQuery.query.loading}
                      isDragginGlobal={isDraggingGlobal}
                      tableState={addTableState}
                      selectBag={addTableSelectBag}
                    />
                  </div>
                )}
                {showAddSearchResultDialog && (
                  <ContentUrlAddAllDialog
                    contentTask={contentTask}
                    clientUrl={clientUrl}
                    searchKeywordCount={keywordQuery.count}
                    searchTerm={debouncedSearchTerm}
                    openDialog={showAddSearchResultDialog}
                    setOpenDialog={setShowAddSearchResultDialog}
                    reload={localReload}
                  />
                )}
              </div>
            </div>
          </DragDropContext>
        </div>
      </VoxelDialogContent>
    </VoxelDialog>
  );
};

export default ContentTaskDialog;
